import { streamDashboardPath, type StreamForDashboard } from 'Lessons';
import { type Playlist } from 'Playlists';
import { NextUpMobileOnly } from 'NextUp';
import { memo, useEffect, type FC } from 'react';
import { Text } from 'Text';
import { useTranslation } from 'react-i18next';
import { type ProgressStatus } from 'ContentProgress';
import { useMatch } from 'react-router-dom';
import { CourseList } from 'CourseItemBox';
import './locales/currentlyStudying/currentlyStudying-en.json';
import { useAppHeaderViewModel } from 'NavigationHelpers';
import { useTargetBrandConfig } from 'FrontRoyalAngular';
import useIsMobileMixin from 'StudentNetwork/react/useIsMobileMixin';
import { type LearnerProject } from 'LearnerProjects';
import { animatePathChange } from 'RouteAnimation';
import { useGetActivePlaylistStreams } from './hooks/useGetActivePlaylistStreams';

type CurrentlyStudyingPresentationProps = {
    streams: StreamForDashboard[];
    playlist: Playlist | null;
    onPress: (course: StreamForDashboard) => void;
    getProgressInfo: ({
        type,
        content,
    }: { type: 'stream'; content: StreamForDashboard } | { type: 'project'; content: LearnerProject }) => {
        status: ProgressStatus;
    } | null;
    numCourses: number;
    numExams: number;
    numComplete: number;
    projects: LearnerProject[];
    NextUpMobileBox: React.ReactNode;
};

export const CurrentlyStudyingPresentation: FC<CurrentlyStudyingPresentationProps> = ({
    streams,
    playlist,
    getProgressInfo,
    onPress,
    numCourses,
    numExams,
    numComplete,
    projects,
    NextUpMobileBox,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'currentlyStudying.currentlyStudying' });

    if (!playlist) return null;

    return (
        <div className="currently-studying flex flex-col p-3.5 pt-[30px] md:p-[30px]">
            <div className="mb-[30px] md:hidden">{NextUpMobileBox}</div>

            <Text
                variant="h1"
                weight="semibold"
                uppercase
                color="black"
                className="mb-1 hidden text-center md:block md:text-start"
            >
                {playlist.title}
            </Text>
            <Text
                variant="h2"
                weight="semibold"
                uppercase
                color="black"
                className="mb-1 block text-center md:hidden md:text-start"
            >
                {playlist.title}
            </Text>

            <Text uppercase className="mb-[30px] text-center md:mb-[15px] md:text-start">
                {t('playlist_status_courses', { num: numCourses })}
                {t('playlist_status_exams', { num: numExams })}
                {!!projects.length && t('playlist_status_projects', { num: projects.length })}
                {t('playlist_status_complete', { num: numComplete })}
            </Text>

            <Text className="mb-[30px] hidden md:block">{playlist.description}</Text>

            <CourseList
                fullWidth
                courses={streams}
                onPress={onPress}
                getProgressInfo={getProgressInfo}
                projects={projects}
                showSubtitle
                availableOffline
            />
        </div>
    );
};

export const CurrentlyStudying: FC = memo(() => {
    const { activePlaylistStreams, activePlaylist, getProgressInfo, numCourses, numExams, numComplete, projects } =
        useGetActivePlaylistStreams();
    const onPress = (stream: StreamForDashboard) => {
        animatePathChange(streamDashboardPath({ streamTitle: stream.title, streamId: stream.id }));
    };
    const AppHeaderViewModel = useAppHeaderViewModel();
    const brandConfig = useTargetBrandConfig();
    const { windowInnerWidth } = useIsMobileMixin();
    const { t } = useTranslation('back_royal');
    const isCurrentlyStudyingRoute = useMatch('/dashboard/currently-studying');

    useEffect(() => {
        if (!isCurrentlyStudyingRoute) return;

        const requiresShortTitle = windowInnerWidth <= 400;
        const institutionName = requiresShortTitle
            ? brandConfig.brandNameShort
            : brandConfig[brandConfig.appHeaderHomeTitleNameProperty];

        AppHeaderViewModel.learningBoxMode = { mode: 'active_playlist' };
        AppHeaderViewModel.setTitleHTML(
            t('lessons.stream.student_dashboard_learning_box.homeTitle', { institutionName }).toLocaleUpperCase(),
            {
                small: !requiresShortTitle,
            },
        );
    }, [AppHeaderViewModel, brandConfig, isCurrentlyStudyingRoute, windowInnerWidth, t]);

    return (
        <CurrentlyStudyingPresentation
            streams={activePlaylistStreams}
            playlist={activePlaylist}
            getProgressInfo={getProgressInfo}
            onPress={onPress}
            numCourses={numCourses}
            numExams={numExams}
            numComplete={numComplete}
            projects={projects}
            NextUpMobileBox={<NextUpMobileOnly />}
        />
    );
});
